import React from 'react';
import { GitBranch, ChevronDown, X, Plus, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import { useFlowStore } from '../../store';
import { Condition } from '../../types'; // Import the new Condition interface
import CustomSelect from '../common/CustomSelect';
import StepConfigurationModal from './StepConfigurationModal';
import { useProjectStore } from '../../store';
import { useModels } from '../../hooks/queries/useModels';
import { Node, Edge } from 'reactflow';
import apiService from '../../services/api';
import {
  normalizeProperty,
  isNumericType,
  type PropertyMeta,
} from '../../utils/propertyMeta';

interface ConditionBuilderProps {
  conditions: Condition[]; // Use the new Condition interface
  onChange: (conditions: Condition[]) => void; // Update onChange to use Condition[]
  previousStep?: any;
  index?: number; // Add index prop for current condition
  currentNodeId?: string; // Add current node ID for creating new conditional steps
  workflowId?: number; // Add workflow ID for creating new steps
  /**
   * AC-1273 / AC-1280: when true, the field picker groups options under
   * "Element fields" (paths on the element class) and "Parent fields" (paths
   * on the parent envelope, excluding sourceFieldPath). Used by filter step
   * configuration so authors can reference both element and parent fields in
   * a per-element predicate.
   */
  isArrayInput?: boolean;
  /**
   * Name of the element class resolved from the parent's list[X] field, e.g.
   * "DomainValuation". Element-scoped picker entries are emitted as
   * `${elementContextClassName}.${field}`.
   */
  elementContextClassName?: string;
  /**
   * The parent-envelope field path being iterated (e.g. "domain_valuations").
   * Excluded from the "Parent fields" picker entries because it IS the array
   * we're iterating, not a scalar field to reference.
   */
  sourceFieldPath?: string;
  /**
   * AC-1290: the persisted orchestration_step_id of the conditional step this
   * builder is editing. When provided and any condition targets an array
   * element, the advisor banner's "Convert to Filter" CTA is enabled. When
   * absent (pre-save), the CTA is disabled and instructs the user to save.
   */
  stepId?: number;
  /**
   * AC-1290: invoked when the user clicks the advisor banner's CTA. Receives
   * the stepId to convert. When omitted, the builder falls back to calling
   * ``apiService.convertConditionalToFilter`` directly.
   */
  onConvertToFilter?: (stepId: number) => Promise<void> | void;
}

const ConditionBuilder: React.FC<ConditionBuilderProps> = (props) => {
  const {
    conditions, onChange, previousStep, index, currentNodeId, workflowId,
    isArrayInput = false,
    elementContextClassName,
    sourceFieldPath,
    stepId,
    onConvertToFilter,
  } = props;
  const { nodes, edges, addNode, addEdge } = useFlowStore();
  // AC-1412 / AC-1377 P5.1: project models flow through React Query
  // (`useModels`) instead of `useProjectStore.models`. The `agents` slice
  // was destructured but never read here, so it is dropped entirely.
  const currentProjectId = useProjectStore((state) => state.currentProject?.project_id);
  const modelsQuery = useModels(currentProjectId);
  const models = React.useMemo(() => modelsQuery.data ?? [], [modelsQuery.data]);
  const [showNewStepModal, setShowNewStepModal] = React.useState(false);

  // AC-1290: detect any condition whose field references an array element.
  // The condition picker (flattenProperties) already tags entries with
  // ``isArrayElement: true`` when descending through a ``list[X]`` parent
  // field. We also check whether the authored condition has this flag.
  const hasArrayElementCondition = React.useMemo(
    () => conditions.some((c: any) => c && c.isArrayElement === true),
    [conditions],
  );
  const showAdvisorBanner = !isArrayInput && hasArrayElementCondition;
  const canConvertToFilter = showAdvisorBanner && stepId != null;
  const [convertingViaBanner, setConvertingViaBanner] = React.useState(false);

  const handleBannerConvert = React.useCallback(async () => {
    if (!canConvertToFilter || stepId == null) return;
    setConvertingViaBanner(true);
    try {
      if (onConvertToFilter) {
        // AC-1293: prefer the parent-provided handler so the WorkflowEditor
        // can update the React Flow node, show a success toast, and reopen
        // the modal on the converted filter step.
        await onConvertToFilter(stepId);
      } else if (workflowId != null) {
        // Fallback path used only in isolated unit tests that render the
        // ConditionBuilder without a parent handler. Production renders via
        // StepConfigurationModal always pass onConvertToFilter.
        console.warn(
          '[ConditionBuilder] onConvertToFilter prop not provided; falling back to direct API call without UI sync.',
        );
        await apiService.convertConditionalToFilter(workflowId, stepId);
      } else {
        console.warn(
          '[ConditionBuilder] handleBannerConvert invoked without onConvertToFilter or workflowId; no-op.',
        );
      }
    } catch (error: any) {
      console.error('[ConditionBuilder] Failed to convert conditional to filter:', error);
      const message =
        error?.response?.data?.detail || error?.message || 'Failed to convert step to Filter';
      toast.error(message);
    } finally {
      setConvertingViaBanner(false);
    }
  }, [canConvertToFilter, stepId, onConvertToFilter, workflowId]);

  const getAllFields = React.useCallback((): { fields: Record<string, any>, className: string } => {
    if (!previousStep) return { fields: {}, className: '' };

    let rootProperties: Record<string, any> = {};
    let className = '';

    if (previousStep.step_type === 'run_agent') {
      const previousNode = nodes.find(node => node.id === String(previousStep.orchestration_step_id));
      const agent = previousNode?.data?.agent || previousStep.agent;

      if (agent?.output_model?.properties) {
        rootProperties = agent.output_model.properties;
        className = agent.output_model.class_name || '';
      } else if (previousStep.output_model?.properties) {
        // Fallback to step's output model if agent not found or doesn't have output model
        rootProperties = previousStep.output_model.properties;
        className = previousStep.output_model.class_name || '';
      }
    } else if (previousStep.output_model?.properties) {
      rootProperties = previousStep.output_model.properties;
      className = previousStep.output_model.class_name || '';
    }

    // Recursively flatten nested properties with dot notation
    const flattenProperties = (properties: Record<string, any>, prefix: string = '', depth: number = 0, insideArray: boolean = false): Record<string, any> => {
      const flattened: Record<string, any> = {};
      
      // Debug: Log the properties being processed
      if (import.meta.env.DEV && depth === 0) {
        console.log('[ConditionBuilder] Processing root properties:', properties);
      }
      
      for (const [key, value] of Object.entries(properties)) {
        const fullPath = prefix ? `${prefix}.${key}` : key;
        
        // Check if this field references another model FIRST
        // Extract potential class name from string values like "EmailSummary" or "list[Email]"
        const valueClassName = typeof value === 'string'
          ? (value.match(/^(?:list|List)\[(.+)\]$/)?.[1] || value)
          : null;
        const matchesProjectModel = valueClassName
          ? models?.some(m => m.class_name === valueClassName)
          : false;

        const isModelReference = (
          matchesProjectModel ||
          key.endsWith('_type') ||
          key.endsWith('_model') ||
          key.endsWith('_ref') ||
          (value && typeof value === 'object' && value.type === 'object') ||
          (value && typeof value === 'object' && typeof value.type === 'string' && value.type.includes('Type')) ||
          (typeof value === 'string' && value.includes('Type'))
        );
        
        // Debug logging
        if (import.meta.env.DEV) {
          console.log(`[ConditionBuilder] Processing field: ${key}`, {
            value,
            isModelReference,
            valueType: typeof value,
            valueTypeString: typeof value === 'object' ? value?.type : value
          });
        }
        
        // AC-1280: Detect descent through a list[X] parent field. When true,
        // mark the nested descendants with isArrayElement=true so conditional
        // mode can show a warning badge; filter mode can scope the picker.
        // AC-1450: also recognize the rich-dict shape ({type, description, ...})
        // emitted by recommended-schema flows so filter/conditional can step
        // into list elements declared in either format.
        const valueTypeStr: string | null =
          typeof value === 'string'
            ? value
            : value && typeof value === 'object' && typeof (value as any).type === 'string'
              ? (value as any).type
              : null;
        const isListReference = !!(valueTypeStr && /^(?:list|List)\[.+\]$/.test(valueTypeStr));
        const nowInsideArray = insideArray || isListReference;

        // When descending through list[X], try to pull in the element class's
        // nested_models properties so descendant fields carry isArrayElement=true
        // in conditional mode (AC-1280). This powers the warning badge.
        if (isListReference) {
          const inner = (valueTypeStr as string).match(/^(?:list|List)\[(.+)\]\s*$/);
          const elementName = inner ? inner[1].trim() : null;
          if (elementName) {
            // Prefer the upstream step's nested_models declaration.
            const upstreamModel = (previousStep as any)?.agent?.output_model
              || (previousStep as any)?.output_model;
            const nestedProps: Record<string, any> | undefined =
              upstreamModel?.nested_models?.[elementName]?.properties;
            if (nestedProps) {
              const nested = flattenProperties(nestedProps, fullPath, depth + 1, true);
              Object.assign(flattened, nested);
            } else {
              // Fallback to project-store model lookup.
              const foundModel = models?.find((m) => m.class_name === elementName);
              if (foundModel?.properties) {
                const nested = flattenProperties(
                  foundModel.properties as any,
                  fullPath,
                  depth + 1,
                  true,
                );
                Object.assign(flattened, nested);
              }
            }
          }
        }

        if (isModelReference) {
          // DON'T add the model reference field itself to flattened
          // Instead, only add its nested fields

          // Try to find the actual model definition first
          if (value && typeof value === 'object' && value.properties) {
            // This model has its own properties defined
            const nestedFlattened = flattenProperties(value.properties, fullPath, depth + 1, nowInsideArray);
            Object.assign(flattened, nestedFlattened);
          } else {
            // Try to find the model in the project store
            let foundModel = null;
            let modelClassName: string | null = null;
            
            // Extract model class name from various formats (including list[Email] -> Email)
            if (typeof value === 'string') {
              const listInner = value.match(/^(?:list|List)\[(.+)\]$/);
              modelClassName = listInner ? listInner[1] : value;
            } else if (value && typeof value === 'object' && typeof value.type === 'string') {
              modelClassName = value.type;
            } else if (value && typeof value === 'object' && value.class_name) {
              modelClassName = value.class_name;
            }
            
            // Clean up the class name (remove parentheses, etc.)
            if (modelClassName) {
              modelClassName = modelClassName.replace(/[()]/g, '').trim();
              foundModel = models?.find(model => model.class_name === modelClassName);
            }
            
            if (foundModel && foundModel.properties) {
              // Use the actual model properties
              const nestedFlattened = flattenProperties(foundModel.properties, fullPath, depth + 1, nowInsideArray);
              Object.assign(flattened, nestedFlattened);
            } else {
              // For known model types like GenreType, add the expected fields
              if (modelClassName === 'GenreType') {
                const genreTypeFields = [
                  { name: 'name', type: 'string' },
                  { name: 'description', type: 'string' }
                ];

                genreTypeFields.forEach(field => {
                  flattened[`${fullPath}.${field.name}`] = {
                    type: field.type,
                    description: `${field.name} property of ${modelClassName}`,
                    isNestedModelField: true,
                    isArrayElement: nowInsideArray,
                  };
                });
              } else {
                // Fallback: Add common model fields that are typically available
                const commonModelFields = [
                  { name: 'name', type: 'string' },
                  { name: 'description', type: 'string' },
                  { name: 'id', type: 'string' },
                  { name: 'value', type: 'string' },
                  { name: 'label', type: 'string' },
                  { name: 'code', type: 'string' },
                  { name: 'is_active', type: 'boolean' },
                  { name: 'created_at', type: 'string' },
                  { name: 'updated_at', type: 'string' }
                ];
                
                commonModelFields.forEach(field => {
                  flattened[`${fullPath}.${field.name}`] = {
                    type: field.type,
                    description: `${field.name} property of ${modelClassName || key}`,
                    isNestedModelField: true,
                    isArrayElement: nowInsideArray,
                  };
                });
              }
            }
          }

          // Skip adding the model reference field itself - continue to next iteration
          continue;
        }

        // Add the current property (only if it's not a model reference).
        // AC-1280: when descending through a list[X] field, tag the entry as
        // isArrayElement so downstream UI can warn or scope correctly.
        if (typeof value === 'object' && value !== null) {
          flattened[fullPath] = { ...value, isArrayElement: insideArray };
        } else {
          flattened[fullPath] = insideArray
            ? { type: value, isArrayElement: true }
            : value;
        }

        // If this property has nested properties, recursively flatten them
        if (value && typeof value === 'object' && value.properties) {
          const nestedFlattened = flattenProperties(value.properties, fullPath, depth + 1, insideArray);
          Object.assign(flattened, nestedFlattened);
        }
      }

      return flattened;
    };

    const fields = flattenProperties(rootProperties);
    return { fields, className };
  }, [previousStep, nodes, models]);

  /**
   * AC-1280: When ``isArrayInput`` is true, build the set of fields on the
   * *element* class (resolved from ``list[X]`` on the parent's source_field).
   * Returns a map of field path -> type metadata, keyed by ``${element}.${field}``.
   */
  const getElementFields = React.useCallback((): Record<string, any> => {
    if (!isArrayInput || !elementContextClassName) return {};

    // Prefer nested_models on the previous step's output_model, then project models.
    const previousOutputModel =
      previousStep?.agent?.output_model ||
      (previousStep?.step_type === 'run_agent'
        ? nodes.find((n) => n.id === String(previousStep.orchestration_step_id))?.data?.agent?.output_model
        : undefined) ||
      previousStep?.output_model;

    let elementProps: Record<string, any> | null = null;
    const nested = previousOutputModel?.nested_models?.[elementContextClassName];
    if (nested?.properties) {
      elementProps = nested.properties;
    }
    if (!elementProps) {
      const model = models?.find((m) => m.class_name === elementContextClassName);
      if (model?.properties) elementProps = model.properties as any;
    }
    if (!elementProps) return {};

    const out: Record<string, any> = {};
    for (const [key, value] of Object.entries(elementProps)) {
      out[`${elementContextClassName}.${key}`] = value;
    }
    return out;
  }, [isArrayInput, elementContextClassName, previousStep, nodes, models]);

  const getAvailableFields = React.useCallback((currentField?: string): Record<string, any> => {
    const { fields, className } = getAllFields();
    const availableFields = { ...fields };
    
    // Create a set of used field paths (with class name prefix)
    const usedFields = new Set(
      conditions
        .map(cond => cond.field)
        .filter(f => f && f !== currentField)
    );
    
    // Remove used fields from available fields
    // We need to check both the raw field path and the full path with class name
    Object.keys(availableFields).forEach(field => {
      const fullField = className ? `${className}.${field}` : field;
      if (usedFields.has(fullField) || usedFields.has(field)) {
        delete availableFields[field];
      }
    });
    
    return availableFields;
  }, [getAllFields, conditions]);

  const { fields: allFields, className } = React.useMemo(() => getAllFields(), [getAllFields]);

  const availableFields = React.useMemo(() => getAvailableFields(), [getAvailableFields]);

  const addCondition = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Add a new empty Condition object
    onChange([...conditions, { field: '', operator: '', value: '' }]);
    // No need to update selectedModels here as the new condition will have an empty field
  };

  const updateCondition = (index: number, updatedPart: Partial<Condition>) => {
    const newConditions = [...conditions];
    newConditions[index] = { ...newConditions[index], ...updatedPart };
    onChange(newConditions);
  };

  const removeCondition = (index: number) => {
    const newConditions = conditions.filter((_, i) => i !== index);
    onChange(newConditions);
  };

  const getTypeString = (type: any): string => {
    if (typeof type === 'string') return type;
    if (typeof type === 'object' && type !== null) {
      if (type.literalValues) {
        return `Literal[${type.literalValues.join(',')}]`; // Indicate it's a Literal and list values
      }
      if (type.type) return type.type;
      return JSON.stringify(type);
    }
    return 'unknown';
  };

  const getLiteralValues = (type: any): string[] => {
    if (typeof type === 'object' && type !== null) {
      if (type.literalValues) {
        return type.literalValues;
      }
      if (type.type?.startsWith('Literal[')) {
        const values = type.type.slice(8, -1).split(',').map((v: string) => v.trim());
        return values;
      }
    }
    if (typeof type === 'string' && type.startsWith('Literal[') && type.endsWith(']')) {
      const values = type.slice(8, -1).split(',').map(v => v.trim());
      return values;
    }
    return [];
  };

  // AC-1300: Translate a condition field (possibly element-scoped in filter mode)
  // into the key used in `allFields`. Conditional mode keys entries with the
  // parent path (e.g. "domain_valuations.final_score"); filter mode authors
  // conditions using the element class prefix (e.g. "DomainValuation.final_score")
  // so we rewrite that prefix back to the source-field prefix before lookup.
  const resolveFieldLookupPath = React.useCallback((conditionField: string): string => {
    if (!conditionField || !conditionField.includes('.')) return conditionField;
    const segments = conditionField.split('.');
    if (
      isArrayInput &&
      elementContextClassName &&
      sourceFieldPath &&
      segments[0] === elementContextClassName
    ) {
      return [sourceFieldPath, ...segments.slice(1)].join('.');
    }
    return segments.slice(1).join('.');
  }, [isArrayInput, elementContextClassName, sourceFieldPath]);

  // This function will now construct the string from a Condition object
  const constructConditionString = (condition: Condition): string => {
    const { field, operator, value } = condition;
    if (!field || !operator) return ''; // Incomplete condition

    // Extract the field path for lookup in the flattened field map.
    const fieldPath = resolveFieldLookupPath(field);
    const fieldSchema = allFields[fieldPath];
    
    // Handle regular field types - since nested paths are now directly in the dropdown
    const fieldType = fieldSchema ? getTypeString(fieldSchema).toLowerCase() : '';
    let formattedValue = value;
    
    if (fieldType.includes('string') || fieldType.includes('str')) {
      formattedValue = `"${value}"`;
    } else if (fieldType.includes('bool')) {
      // Format as Python boolean constants (capitalized True/False)
      const boolStr = typeof value === 'boolean' ? value : String(value).toLowerCase() === 'true';
      formattedValue = boolStr ? 'True' : 'False';
    }
    // Numbers and other types can be used directly

    return `${field} ${operator} ${formattedValue}`;
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
        <GitBranch className="w-4 h-4 mr-2" />
        {previousStep ? (
          <span>
            Conditions based on: {previousStep.description || 'Previous step output'}
            {Object.keys(availableFields).length > 0 && (
              <span className="ml-2 text-green-600">
                ({Object.keys(availableFields).length} fields available)
              </span>
            )}
          </span>
        ) : (
          <span className="text-orange-500">No previous step found</span>
        )}
      </div>

      {/*
        AC-1290: Save-time advisor banner. When one or more conditions target
        an array element (isArrayElement=true), surface a yellow banner that
        tells the author the conditional cannot iterate arrays and offers the
        Convert-to-Filter CTA. The CTA is disabled until the step has been
        saved (stepId present) since the backend endpoint requires a
        persisted step_id.
      */}
      {showAdvisorBanner && (
        <div
          role="note"
          data-testid="condition-builder-advisor-banner"
          className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded px-2 py-2"
        >
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" aria-label="warning" />
          <div className="flex-1">
            <div>
              This condition targets an element inside an array. Conditional
              steps can&rsquo;t iterate arrays. Convert this step to a Filter.
            </div>
            <div className="mt-2 flex items-center gap-2">
              <button
                type="button"
                onClick={handleBannerConvert}
                disabled={!canConvertToFilter || convertingViaBanner}
                aria-disabled={!canConvertToFilter || convertingViaBanner}
                title={
                  canConvertToFilter
                    ? 'Convert this conditional to a filter step'
                    : 'Save the step first, then convert to a Filter.'
                }
                className="px-2 py-1 rounded border border-amber-400 bg-amber-100 dark:bg-amber-800/40 text-amber-800 dark:text-amber-100 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {convertingViaBanner ? 'Converting...' : 'Convert to Filter'}
              </button>
              {!canConvertToFilter && (
                <span className="text-[10px] text-amber-600 dark:text-amber-400">
                  Save the step first, then convert.
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* AC-1280: In isArrayInput mode, surface the element-class + parent-envelope
          field groups so authors know what is available before opening the picker. */}
      {isArrayInput && (
        <div
          className="rounded border border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-900/20 p-2 text-xs"
          data-testid="condition-builder-array-legend"
        >
          <div className="font-medium text-rose-800 dark:text-rose-200 mb-1">
            Element fields
          </div>
          <div className="text-rose-700 dark:text-rose-300 font-mono break-all">
            {Object.keys(getElementFields()).join(', ') || '(none resolved)'}
          </div>
          <div className="font-medium text-rose-800 dark:text-rose-200 mt-2 mb-1">
            Parent fields
          </div>
          <div className="text-rose-700 dark:text-rose-300 font-mono break-all">
            {Object.keys(availableFields)
              .filter((f) => f !== sourceFieldPath)
              .join(', ') || '(none)'}
          </div>
        </div>
      )}

      {conditions.map((condition, index) => {
        // AC-1254: Resolve PropertyMeta for the currently selected field so the
        // hint + range warning can render below the condition row.
        const fieldPath = condition.field && condition.field.includes('.')
          ? condition.field.split('.').slice(1).join('.')
          : (condition.field || '');
        const fieldSchema = fieldPath ? allFields[fieldPath] : undefined;
        const meta: PropertyMeta | null = fieldSchema !== undefined
          ? normalizeProperty(fieldSchema as any)
          : null;

        // Build the hint line. Only render if we have a description or range.
        // Format examples:
        //   "final_score (int, 0-100): Composite quality score"
        //   "tier (str): Valuation tier"
        //   "confidence (float, 0-1)"
        const hasRange = meta !== null && (meta.min !== undefined || meta.max !== undefined);
        const hasDescription = meta !== null && !!meta.description;
        // AC-1254 refinement: when only one bound is declared, render it as
        // ">= min" / "<= max" rather than the ambiguous "min-" / "-max" that
        // the naive join produced. Only emit the "min-max" range when both
        // bounds are set.
        const formatRange = (min: number | undefined, max: number | undefined): string => {
          if (min !== undefined && max !== undefined) return `${min}-${max}`;
          if (min !== undefined) return `>= ${min}`;
          if (max !== undefined) return `<= ${max}`;
          return '';
        };
        let hintText: string | null = null;
        if (meta !== null && (hasRange || hasDescription)) {
          const rangeStr = hasRange ? `, ${formatRange(meta.min, meta.max)}` : '';
          const typePart = `(${meta.type}${rangeStr})`;
          const descPart = hasDescription ? `: ${meta.description}` : '';
          hintText = `${fieldPath} ${typePart}${descPart}`;
        }

        // Build the range warning. Fires when:
        //   - meta has numeric type AND a declared min/max
        //   - condition.value is a finite number (not a field reference)
        //   - parsed value lies outside [min, max]
        const rawValue = condition.value;
        let warningText: string | null = null;
        if (
          meta !== null &&
          isNumericType(meta.type) &&
          (meta.min !== undefined || meta.max !== undefined)
        ) {
          // Detect field reference RHS: string starting with a known class name
          // prefix (e.g. "DomainValuation.threshold"). Numbers are never refs.
          const isFieldReference =
            typeof rawValue === 'string' &&
            className.length > 0 &&
            rawValue.startsWith(`${className}.`);
          if (!isFieldReference) {
            const parsed =
              typeof rawValue === 'number'
                ? rawValue
                : typeof rawValue === 'string' && rawValue.trim() !== ''
                ? Number(rawValue)
                : Number.NaN;
            if (Number.isFinite(parsed)) {
              const below = meta.min !== undefined && parsed < meta.min;
              const above = meta.max !== undefined && parsed > meta.max;
              if (below || above) {
                const rangeFmt = formatRange(meta.min, meta.max);
                const direction = below
                  ? 'below'
                  : above
                  ? 'above'
                  : 'outside';
                warningText = `Value ${parsed} is ${direction} the documented range of ${rangeFmt}. Threshold may not behave as expected.`;
              }
            }
          }
        }

        return (
        <div key={index} className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          {/* Field Selection with Hierarchical Options */}
          <CustomSelect
            className="w-80"
            value={condition.field || ''}
            onChange={(value) => {
              // AC-1273 / AC-1280: defensive guard. The group-header rows in
              // the isArrayInput picker are rendered as non-interactive
              // (disabled) options in CustomSelect, but double-gate here so
              // any future regression in CustomSelect cannot let a
              // `__element_header__` / `__parent_header__` pseudo-value leak
              // into condition.field and cause a runtime NameError during AST
              // evaluation.
              if (value === '__element_header__' || value === '__parent_header__') {
                return;
              }
              updateCondition(index, { field: value, operator: '', value: '' });
            }}
            placeholder="Select a field..."
            options={(() => {
              const fieldOptions: { value: string; label: string; disabled?: boolean }[] = [];

              // AC-1273 / AC-1280: dual-source picker for filter (isArrayInput=true).
              // Render two groups: "Element fields" (from element class) and
              // "Parent fields" (from parent envelope, minus sourceFieldPath).
              // Headers are marked `disabled: true` so they render as
              // non-interactive dividers (cannot be clicked or keyboard-selected).
              if (isArrayInput) {
                const elementFields = getElementFields();
                if (Object.keys(elementFields).length > 0) {
                  fieldOptions.push({
                    value: '__element_header__',
                    label: 'Element fields',
                    disabled: true,
                  });
                  Object.keys(elementFields).forEach((fullField) => {
                    fieldOptions.push({ value: fullField, label: `  ${fullField}` });
                  });
                }
                const parentEntries = Object.entries(getAvailableFields(condition.field)).filter(
                  ([field]) => field !== sourceFieldPath,
                );
                if (parentEntries.length > 0) {
                  fieldOptions.push({
                    value: '__parent_header__',
                    label: 'Parent fields',
                    disabled: true,
                  });
                  parentEntries.forEach(([field]) => {
                    const fullField = className ? `${className}.${field}` : field;
                    fieldOptions.push({ value: fullField, label: `  ${fullField}` });
                  });
                }
                return fieldOptions;
              }

              // Show the currently selected field if it exists but isn't in available fields
              if (condition.field && !Object.keys(getAvailableFields(condition.field)).some(field => {
                const fullField = className ? `${className}.${field}` : field;
                return fullField === condition.field;
              })) {
                fieldOptions.push({ value: condition.field, label: condition.field });
              }

              // Show all available fields with hierarchical grouping
              Object.entries(getAvailableFields(condition.field)).forEach(([field, fieldSchema]) => {
                const fullField = className ? `${className}.${field}` : field;

                // Check if this is an expandable object/model reference
                const isExpandable = fieldSchema && typeof fieldSchema === 'object' &&
                  (fieldSchema.type === 'object' || fieldSchema.properties ||
                   field.endsWith('_type') || field.endsWith('_model') || field.endsWith('_ref') ||
                   (typeof fieldSchema.type === 'string' && fieldSchema.type.includes('Type')));

                // Add the main field option
                fieldOptions.push({ value: fullField, label: fullField });

                // If expandable, add nested field options with indentation
                if (isExpandable) {
                  const commonModelFields = [
                    'name', 'description', 'id', 'value', 'label', 'code',
                    'is_active', 'created_at', 'updated_at'
                  ];

                  commonModelFields.forEach(nestedField => {
                    const nestedFullField = `${fullField}.${nestedField}`;
                    fieldOptions.push({ value: nestedFullField, label: `  \u21B3 ${nestedFullField}` });
                  });
                }
              });

              return fieldOptions;
            })()}
          />

          {/* Operator Selection (dynamic based on field type) */}
          {condition.field && (
            <CustomSelect
              className="w-24"
              value={condition.operator}
              onChange={(value) => updateCondition(index, { operator: value })}
              placeholder="Op"
              options={(() => {
                const fieldPath = resolveFieldLookupPath(condition.field);
                const fieldType = allFields[fieldPath] ? getTypeString(allFields[fieldPath]).toLowerCase() : '';
                if (fieldType.includes('bool')) {
                  return [
                    { value: '==', label: 'is' },
                    { value: '!=', label: 'is not' },
                  ];
                } else if (fieldType.includes('string') || fieldType.includes('str')) {
                  return [
                    { value: '==', label: '==' },
                    { value: '!=', label: '!=' },
                    { value: 'in', label: 'in' },
                    { value: 'not in', label: 'not in' },
                  ];
                } else if (fieldType.includes('int') || fieldType.includes('number') || fieldType.includes('float')) {
                  return [
                    { value: '==', label: '==' },
                    { value: '!=', label: '!=' },
                    { value: '>', label: '>' },
                    { value: '<', label: '<' },
                    { value: '>=', label: '>=' },
                    { value: '<=', label: '<=' },
                  ];
                } else {
                  return [
                    { value: '==', label: '==' },
                    { value: '!=', label: '!=' },
                  ];
                }
              })()}
            />
          )}

          {/* Value Input (dynamic based on field type) */}
          {condition.field && condition.operator && (
            (() => {
              // Extract the field path for lookup in the flattened field map.
              const fieldPath = resolveFieldLookupPath(condition.field);
              const fieldSchema = allFields[fieldPath];
              
              const fieldType = fieldSchema ? getTypeString(fieldSchema) : '';
              const literalValues = getLiteralValues(fieldSchema);

              if (fieldType.includes('Literal') || literalValues.length > 0) {
                return (
                  <CustomSelect
                    className="w-48"
                    value={String(condition.value)}
                    onChange={(value) => updateCondition(index, { value })}
                    placeholder="Select value"
                    options={literalValues.map((literalValue) => ({
                      value: String(literalValue),
                      label: String(literalValue),
                    }))}
                  />
                );
              } else if (fieldType.includes('bool')) {
                return (
                  <CustomSelect
                    className="w-32"
                    value={String(condition.value)}
                    onChange={(value) => updateCondition(index, { value: value === 'true' })}
                    placeholder="Select value"
                    options={[
                      { value: 'true', label: 'True' },
                      { value: 'false', label: 'False' },
                    ]}
                  />
                );
              } else if (fieldType.includes('string') || fieldType.includes('str')) {
                return (
                  <input
                    type="text"
                    className="input input-sm w-48"
                    placeholder="Enter value"
                    value={condition.value}
                    onChange={(e) => updateCondition(index, { value: e.target.value })}
                  />
                );
              } else if (fieldType.includes('int') || fieldType.includes('number') || fieldType.includes('float')) {
                return (
                  <input
                    type="number"
                    className="input input-sm w-32"
                    placeholder="Enter number"
                    value={condition.value}
                    onChange={(e) => updateCondition(index, { value: Number(e.target.value) })}
                  />
                );
              } else {
                return (
                  <input
                    type="text"
                    className="input input-sm w-48"
                    placeholder="Enter value"
                    value={condition.value}
                    onChange={(e) => updateCondition(index, { value: e.target.value })}
                  />
                );
              }
            })()
          )}
          
          <button
            onClick={() => removeCondition(index)}
            className="btn btn-ghost btn-sm"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        {/* AC-1254: Inline field hint (type + optional range + optional description) */}
        {hintText && (
          <div
            className="text-xs text-gray-600 dark:text-gray-400 pl-1"
            data-testid={`condition-field-hint-${index}`}
          >
            {hintText}
          </div>
        )}
        {/* AC-1254: Soft warning when a numeric literal lies outside the declared range.
            Warning is non-blocking - the user can still save the step. */}
        {warningText && (
          <div
            role="alert"
            className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded px-2 py-1"
            data-testid={`condition-range-warning-${index}`}
          >
            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>{warningText}</span>
          </div>
        )}
        </div>
        );
      })}

      {Object.keys(availableFields).length === 0 && conditions.length === 0 && (
        <div className="text-sm text-gray-500 dark:text-gray-400 p-3 bg-gray-50 dark:bg-gray-900 rounded border">
          <p>No fields available from the previous step.</p>
          <p className="mt-1">Make sure the previous step has an agent with an output model defined.</p>
        </div>
      )}

      {/*
        AC-1280: In conditional mode (not isArrayInput), warn when the previous
        step's model exposes list[X] fields. Conditional cannot iterate arrays,
        so element-level predicates fail at runtime. The filter step type is
        the appropriate tool for per-element filtering.
      */}
      {!isArrayInput &&
        Object.values(allFields).some((v: any) => v && typeof v === 'object' && v.isArrayElement) && (
          <div
            role="note"
            title="Use a filter step to apply per-element predicates to array fields"
            data-testid="condition-builder-array-warning"
            className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded px-2 py-1"
          >
            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" aria-label="warning" />
            <span>
              This step exposes array element fields. Conditional cannot iterate arrays
              &mdash; use a filter step to apply per-element predicates.
            </span>
          </div>
        )}

      <button
        onClick={addCondition}
        className="btn btn-outline btn-sm w-full"
        disabled={!isArrayInput && Object.keys(availableFields).length === 0}
        type="button"
      >
        Add Condition
      </button>
    </div>
  );
};

export default ConditionBuilder;